import {
	Badge,
	type DefaultImageProps,
	type DefaultLinkProps,
	Icon,
	Image,
	type ImageProps,
	Link
} from "@pamperedchef/design-system";
import { className } from "@/app/utilities/className";
import { Button as ReactAriaButton } from "react-aria-components";

import {
	CommunityBuzzModal,
	CommunityBuzzModalProps
} from "../CommunityBuzzModal";
import * as styles from "./CommunityBuzzCard.module.scss";

export interface CommunityBuzzCardProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> extends React.HTMLAttributes<HTMLDivElement> {
	author?: string;
	actionLabel?: string;
	coverImage: ImageProps<ImageComponentProps>;
	mediaType?: "image" | "video";
	details?: CommunityBuzzModalProps<LinkComponentProps, ImageComponentProps>;
}

const MEDIA_TYPES = {
	image: <Icon name="image" size={24} />,
	video: <Icon name="video" size={24} />
};

/**
 * CommunityBuzz Carousel
 */
export function CommunityBuzzCard<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	author = "",
	actionLabel = "View",
	coverImage,
	mediaType = "image",
	details = {}
}: CommunityBuzzCardProps<
	ImageComponentProps,
	LinkComponentProps
>): JSX.Element {
	return (
		<CommunityBuzzModal
			{...details}
			triggerElement={
				<ReactAriaButton
					{...className(styles["community-buzz-card"])}
					aria-label="Open Modal"
				>
					<div className={styles["community-buzz-card__media-type"]}>
						{MEDIA_TYPES[mediaType]}
					</div>

					{/* @ts-expect-error - React 19 generic type limitation with Image component */}
					<Image<ImageComponentProps>
						{...coverImage}
						className={styles["community-buzz-card__image"]}
						aspectRatio="2:3"
						alt={coverImage.alt || "Community Buzz Cart Image"}
					/>

					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: "8px"
						}}
					>
						<Badge
							className={styles["community-buzz-card__badge"]}
							label={author || ""}
						/>
						<Link
							className={styles["community-buzz-card__cta"]}
							icon={{ name: "ZoomIn" }}
							kind="tertiary"
							tabIndex={-1}
						>
							{actionLabel}
						</Link>
					</span>
				</ReactAriaButton>
			}
		/>
	);
}
